/* Built In Imports */
import { Radio, RadioGroup } from '@chakra-ui/react';
import { usePathname } from 'next/navigation';
import { forwardRef, useEffect, useState } from 'react';

/* External Imports */
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Link,
  TabList,
  Grid,
  FormControl,
  TabPanel,
  TabPanels,
  Tabs,
  useMultiStyleConfig,
  useTab,
  Text,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { nanoid } from 'nanoid';

/* Internal Imports */
/* Components */
import SectionTitle from '@components/Headings/SectionTitle';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import SingleLineText from '@components/UI/SingleLineText';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import config from '@config';
import MidContainer from '@components/Layout/MidContainer';
import { refineUrlDomain } from '@components/Utility/utils';
import ResponsiveSelect from './ResponsiveSelect';
import parser from 'html-react-parser';

/* Services */
import { searchByFieldId } from '@components/Utility/Shared/SharedService';

/**
 *
 * @param {object} section
 * @returns ProgramDetailsEOE
 */
export const pageCss = {
  en: {
    tabTitle: {
      fontSize: {
        base: '14px',
        md: '20px',
      },
      color: 'red',
    },
    testimonialHeight: 571,
    cardTitle: {
      lineHeight: {
        base: '1.5rem',
        md: '200%',
      },
    },
  },
};

let ProgramDetailsLayout = ({ jsonData, progLocation, lang }) => {
  let inPersonData = [jsonData[0], jsonData[1]]
  let onlineData = [jsonData[2]]
  let isEng = lang === 'en'

  if (progLocation === 'in-person') {
    return <Flex position={'relative'} ml={{ md: '0px', lg: '-7rem' }} width={{ md: '100%', lg: '55rem' }} my={'1rem'} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
      <Box display={{ base: 'none', md: 'block' }} position={'absolute'} height={{ md: '90%' }} left={isEng ? '45%' : { md: '46%', lg: '48%' }} width={'1px'} border={'0.5px solid #AEA493'} />
      {inPersonData?.map((item, j) => {
        return <Box
          my={{ base: '1rem', md: '0px' }}
          width={{ base: '100%', md: j === 0 ? '48%' : '52%' }} fontWeight={'500'} fontFamily={isEng ? 'FedraSansStd-book' : 'MuktaMalar'} >
          <Text
            pl={isEng ? { md: '1rem' } : undefined}
            fontWeight={600} textAlign={isEng ? { base: 'center', md: "left" } : { base: 'center', md: j === 0 ? undefined : 'center' }} color={'#02026C'} fontSize={{ md: '18px' }} lineHeight={{ md: '24px' }}
            mb={{ md: '20px' }}>{item?.category_name}</Text>
          {item?.category_description?.map((category) => {
            return <Flex justifyContent={'center'} alignItems={'center'}
              mt={{ base: '0.5rem', md: '0px' }}
              mb={{ md: '20px' }} w={{ md: "100%" }} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
              <LazyLoadImageComponent src={category.icon === 'clock' ? 'https://www.datocms-assets.com/46272/1745480510-clock_icon.svg' :
                'https://www.datocms-assets.com/46272/1745480495-home_icon.svg'
              } />
              <Text width={{ base: 'auto', md: isEng ? '125px' : '115px' }} mx={'5px'} fontSize={{ md: '16px' }} lineHeight={{ md: "24px" }} color={"#000000"}
                fontWeight={{ md: 400 }}>{category.name}
              </Text>
              <Text
                pl={{ base: '5rem', md: '0px' }}
                textAlign={{ base: 'center', md: 'left' }}
                width={{ base: '100%', md: '270px' }} fontSize={{ base: '14px', md: '16px' }}
                pr={{ base: '5rem', md: '15px' }} lineHeight={{ md: "24px" }} color={'#CF4520'}>
                <Text display={{ base: 'none', md: 'inline-block' }} as={'span'} color={"#000"} pr={'5px'}>: </Text>
                {category.value}</Text>
            </Flex>
          })}
        </Box>
      })}
    </Flex>
  }
  else if (progLocation === 'regional-center') {
    return <Flex position={'relative'} width={'auto'} my={'1rem'} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
      {onlineData?.map((item, j) => {
        return <Box
          width={'100%'} fontWeight={'500'} fontFamily={isEng ? 'FedraSansStd-book' : 'MuktaMalar'} >
          <Text fontWeight={600} textAlign={{ base: 'center', md: j === 0 ? undefined : 'center' }} color={'#02026C'} fontSize={{ md: '18px' }} lineHeight={{ md: '24px' }}
            mb={{ md: '20px' }}>{item?.category_name}</Text>
          {item?.category_description?.map((category) => {
            return <Flex justifyContent={'center'} alignItems={'center'}
              mt={{ base: '1rem', md: isEng ? '10px' : '0px' }}
              w={{ md: "100%" }} flexWrap={'wrap'}>
              <LazyLoadImageComponent src={category.icon === 'clock' ? 'https://www.datocms-assets.com/46272/1745480510-clock_icon.svg' :
                'https://www.datocms-assets.com/46272/1745480495-home_icon.svg'
              } />
              <Text width={'auto'} mx={'5px'} fontSize={{ md: '16px' }} lineHeight={{ md: "24px" }} color={"#000000"}
                fontWeight={{ md: 400 }}>{category.name}
              </Text>
              <Text
                // pl={{ base: '5rem', md: '0px' }}
                textAlign={'center'}
                width={'100%'} fontSize={{ base: '14px', md: '16px' }}
                // pr={{ base: '5rem', md: '15px' }} 
                lineHeight={{ md: "24px" }} color={'#CF4520'}>
                {category.value}</Text>
            </Flex>
          })}
        </Box>
      })}
    </Flex>
  }
}

let updateUrlForInPerson = (url, meditator) => {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set('iso_ie_done', meditator);
  return parsedUrl.toString();
}

const ProgramDetailsLOM = ({ section, region, lang, pageData, sectionId }) => {
  console.log('sectiondata', section);
  let [sectionContent, updateSectionContent] = useState(section);
  const matchPath = usePathname();
  const [noUrl, setNoUrl] = useState('');
  const [yesUrl, setYesUrl] = useState('');
  const [noUrl2, setNoUrl2] = useState('');
  const [yesUrl2, setYesUrl2] = useState('');
  const [isIeDone, setIsIeDone] = useState(true);
  const [selectedProg, setSelectedProg] = useState(null);
  const [progLocation, setProgLocation] = useState('');
  let [districts, setDistricts] = useState(sectionContent?.[22]?.data || [])
  let [selectedDistrict, setSelectedDistrict] = useState({})
  let [selectedCity, setSelectedCity] = useState({})
  let isProd = config.ENV_NAME === 'production'
  
  useEffect(() => {
    if(isProd){
      let tempSection = sectionContent   
      tempSection[23]["data"] = tempSection[23]["data"].map(i => ({
        ...i,
        LinkUrl: i?.LinkUrl2
      }));
      updateSectionContent([...tempSection])
    }
  }, [])

  const handleRadioClick = (event, data) => {
    event.preventDefault();
    if (data?.Status?.toLowerCase() !== 'full') {
      setYesUrl(data.Category);
      setNoUrl(data.Category);
      setSelectedProg(data);
      setProgLocation(data.RefName);
      setYesUrl2('')
      setNoUrl2('')
      setSelectedDistrict({})
      setSelectedCity({})
    }
  };

  const handleRadioClick2 = (event, data) => {
    event.preventDefault();
    if (data?.Status?.toLowerCase() !== 'full') {
      setYesUrl2(data.LinkUrl);
      setNoUrl2(data.LinkUrl);
      setSelectedProg(null);
      setSelectedDistrict({})
      setSelectedCity({})
    }
  };
  const emptySelection = () => {
    setYesUrl('');
    setNoUrl('');
    setYesUrl2('');
    setNoUrl2('');
    setSelectedProg(null);
    setSelectedDistrict({})
    setSelectedCity({})
    setProgLocation('')
  };
  const cardStyle = {
    Open: {
      background: '#FFFFFF',
    },
    Selected: {
      background: '#F5F0E5',
      border: '2px solid #CF4520',
    },
    Full: {
      outline: 'none',
      boxShadow: 'none',
      background: '#F5F0E5',
    },
  };

  const cardStyle2 = {
    Open: {
      boxShadow: '0 0 2px #979797',
      background: '#FFFFFF',
    },
    Selected: {
      boxShadow:
        '0px 0px 0px 2px #be3a1d,3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593)',
      background: '#F5F0E5',
    },
    Full: {
      outline: 'none',
      boxShadow: 'none',
      background: '#F5F0E5',
    },
  };

  const CustomTab = forwardRef((props, ref) => {
    // 1. Reuse the `useTab` hook
    const tabProps = useTab({ ...props, ref });
    const isSelected = !!tabProps['aria-selected'];

    // 2. Hook into the Tabs `size`, `variant`, props
    const styles = useMultiStyleConfig('Tabs', tabProps);
    const Arrow = () => {
      return (
        <Box
          as="span"
          position="absolute"
          width="20px"
          display={isSelected ? 'block' : 'none'}
          height="11px"
          clipPath="polygon(0 0, 50% 100%, 100% 0)"
          left="50%"
          bottom="-10px"
          background="#FBAD2E"
          style={{ transition: 'width 1s' }}
          zIndex="0"
          transition="0.4s linear"
        ></Box>
      );
    };

    return (
      <Button
        __css={styles.tab}
        {...tabProps}
        width="50%"
        color={isSelected ? '#211637 !important' : '#7B7B7B  !important'}
        bg={isSelected ? '#FBAD2E' : '#E8DECB'}
        border="0"
        fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
        fontStyle="normal"
        fontWeight="600"
        fontSize={pageCss[lang]?.tabTitle?.fontSize}
        lineHeight="200%"
        _active={{ bg: '#FBAD2E', border: '0' }}
        _focus={{ bg: '#FBAD2E', border: '0' }}
        pos="relative"
        height={{ base: '50px', sm: '75px' }}
        p={{ base: '8px', sm: '1rem' }}
      >
        {isSelected ? <Arrow /> : ''}

        {tabProps.children}
      </Button>
    );
  });

  return (
    <Box
      id={sectionId}
      backgroundColor="#F1EBDF"
      pt={{ base: '20px', md: '80px', lg: '100px' }}
      minH={790}
    >
      <SectionTitle
        titleObj={sectionContent[0]}
        region={region}
        lang={lang}
        mt="0"
      />
      <Flex
        justifyContent="center"
        alignItems="baseline"
        flexWrap="wrap"
        px={{ base: '20px', lg: '0px' }}
        mx="auto"
      >
        <Box width={{ base: '100%', sm: '22%', md: '37%', xl: '22%' }}>
          {sectionContent[1]?.text && (
            <Box
              display="flex"
              alignItems="flex-start"
              gridGap="10px"
              m="13px auto"
              mb={lang=='en' ? {base: '10px', md:'40px'} : undefined}
            >
              <LazyLoadImageComponent src="https://static.sadhguru.org/d/46272/1697477131-icon_calendar.svg" />
              <SingleLineText
                sectionContent={sectionContent[1]}
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontWeight={lang === 'ta' ? '500' : '400'}
                fontSize="18px"
                my={{ base: '0px', md: '0px' }}
                color="#000"
              />
            </Box>
          )}
          {sectionContent[2]?.text && (
            <Box
              display="flex"
              alignItems="center"
              gridGap="10px"
              m="13px auto"
              mb={lang=='en' ? {base: '10px', md:'30px'} : undefined}
            >
              <LazyLoadImageComponent src="https://static.sadhguru.org/d/46272/1697477136-icon_translation.svg" />
              <SingleLineText
                sectionContent={sectionContent[2]}
                my="0px"
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontWeight={lang === 'ta' ? '500' : '400'}
                fontSize="18px"
                color="#000"
              />
            </Box>
          )}
          {sectionContent[3]?.text && (
            <Box
              display="flex"
              alignItems="center"
              gridGap="10px"
              m="13px auto"
              mb={lang=='en' ? {base: '10px', md:'30px'} : undefined}
            >
              <LazyLoadImageComponent src="https://static.sadhguru.org/d/46272/1697477138-icon_clock_watch.svg" />
              <SingleLineText
                sectionContent={sectionContent[3]}
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontWeight={lang === 'ta' ? '500' : '400'}
                fontSize="18px"
                my="10px"
                color="#000"
              />
            </Box>
          )}
        </Box>
        <Box width={{ base: '100%', sm: '22%', xl: '22%' }}>
          {sectionContent[4]?.text && (
            <Box width={{base: 'auto', md: '279px', lg: '279px'}} display="flex" alignItems="start" gridGap="10px" m="13px auto" mb={lang=='en' ? {base: '10px', md:'40px'} : undefined}>
              <LazyLoadImageComponent src="https://static.sadhguru.org/d/46272/1697477134-icon_pin_location.svg" />
              <SingleLineText
                sectionContent={sectionContent[4]}
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontWeight={lang === 'ta' ? '500' : '400'}
                fontSize="18px"
                my="0px"
                color="#000"
              />
            </Box>
          )}
          {sectionContent[5]?.text && (
            <Box width={{base: 'auto', md: '279px', lg: '279px'}} display="flex" alignItems="start" gridGap="10px" m="13px auto">
              <LazyLoadImageComponent src="https://static.sadhguru.org/d/46272/1697477134-icon_pin_location.svg" />
              <SingleLineText
                sectionContent={sectionContent[5]}
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontWeight={lang === 'ta' ? '500' : '400'}
                fontSize="18px"
                my="0px"
                color="#000"
              />
            </Box>
          )}
        </Box>
      </Flex>
      <Box
        background=" url(https://static.sadhguru.org/d/46272/1694600647-26-3.png) right center no-repeat"
        mt="70px"
      >
        <LazyLoadImageComponent
          width={{ base: '140px', md: '230px' }}
          src="https://static.sadhguru.org/d/46272/1694600647-26-3.png"
          alt="bg"
          position="absolute"
          paddingTop={{ base: '30px', md: '0px' }}
          transform="rotate(180deg)"
        />
        <SectionTitle
          titleObj={sectionContent[6]}
          region={region}
          lang={lang}
        />
        <Box
          display="flex"
          flexDir={{ base: 'column', md: 'row' }}
          maxW="850px"
          py={{ base: '15px', md: '60px' }}
          pt="0"
          px={{ base: '15px', md: 0 }}
          w="full"
          mx="auto"
          gridGap="30px"
          justifyContent="center"
        >
          <Box zIndex={3} textAlign={'center'}>
            <LazyLoadImageComponent
              src={sectionContent[7].image?.url}
              alt={sectionContent[7].image?.alt}
              maxW={{ base: '211px', md: '251px' }}
              maxH={{ base: '204px', md: '258px' }}
            />
          </Box>
          <Box
            width={{ base: 'full', md: '375px' }}
            pt={{ base: '0', md: '40px' }}
          >
            <Box ml={{ base: '0', md: '30px' }}>
              <>
                <StructuredTextParser
                  str={render(sectionContent[8].body)}
                  region={region}
                  lang={lang}
                  fontSize={{ base: '16px', md: '22px' }}
                  color="#000000"
                  fontFamily={
                    lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'
                  }
                  mt="0px"
                  className="linkText"
                  fontWeight={lang=='en' ? '400' : undefined}
                />
              </>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box w="100%" maxW="1070px" mx="auto">
        <Box
          maxW="960px"
          width={{ base: '300px', md: '100%' }}
          m="20px auto"
          id="have-you-completed"
          fontSize={{ base: '24px', md: '30px' }}
          color="#02026C"
          lineHeight="normal"
          fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-medium'}
          paddingTop="0"
          textAlign="center"
        >
          {sectionContent[9]?.text}
        </Box>
        <Tabs
          isFitted
          variant="enclosed"
          p="0"
          className="tabcontent"
          border="none"
          borderRadius="12px"
          paddingBottom="20px"
          id="prgTab"
          defaultIndex={0}
        >
          <TabList borderBottom="none">
            <CustomTab
              style={{ borderRadius: '10px 0 0 0' }}
              onClick={() => {
                emptySelection();
                setIsIeDone(true);
              }}
            >
              {sectionContent[10]?.titleText}
            </CustomTab>
            <CustomTab
              style={{ borderRadius: '0 10px 0 0' }}
              onClick={() => {
                emptySelection();
                setIsIeDone(false);
              }}
            >
              {sectionContent[13]?.titleText}
            </CustomTab>
          </TabList>

          <TabPanels m="0 auto" bg="#fff" borderRadius="15px" mb="50px">
            {/* Tab 1 starts here */}
            <TabPanel px={{ base: '15px', md: '0' }} pt="20px" pb="60px">
              <Box
                color="#02026C"
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontSize={{base: '18px', md:"22px"}}
                lineHeight={'30px'}
                py={{ base: '20px', md: '40px' }}
                px={{ base: '15px', md: '30px' }}
                fontWeight={lang === 'ta' ? 'normal' : 'bold'}
                textAlign="center"
                maxW="630px"
                mx="auto"
              >
                {sectionContent[11]?.text}
              </Box>

              <Grid pt="10px" justifyContent={'center'}>
                <FormControl>
                  <RadioGroup
                    value={yesUrl}
                    onChange={event => setYesUrl(event.target.value)}
                  >
                    <Flex
                      placeContent="center"
                      gridGap={{ base: '7px', md: '30px' }}
                      gridRowGap={{ base: '15px', md: '30px' }}
                      mt="10px"
                      maxW="700px"
                      flexWrap="wrap"
                      flexDirection="row"
                    >
                      {sectionContent[12]?.data?.map((data, i) => {
                        return (
                          <Box
                            as="label"
                            key={nanoid(4)}
                            w={{ base: '40%', md: '279px' }}
                            maxW="279px"
                            minW={{ base: '47%', md: '160px' }}
                            paddingLeft={{
                              base: '12.14px',
                              md: '21.18px',
                            }}
                            border="1px solid #979797"
                            borderRadius="10px"
                            pr={{ base: '11.47px', md: '20px' }}
                            pt={{ base: '16.5px', md: '29px' }}
                            pb={{ base: '19px', md: '27px' }}
                            style={
                              yesUrl === data.Category
                                ? cardStyle.Selected
                                : data?.Status?.toLowerCase() === 'full'
                                ? cardStyle.Full
                                : cardStyle.Open
                            }
                            _hover={{
                              boxShadow:
                                '3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593)',
                            }}
                            position="relative"
                            onClick={event => handleRadioClick(event, data)}
                            cursor={
                              data?.Status?.toLowerCase() === 'full'
                                ? 'cursor'
                                : 'pointer'
                            }
                          >
                            {data?.Status?.toLowerCase() === 'full' && (
                              <Box
                                pos="absolute"
                                top={0}
                                right={0}
                                left={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                background="#494949"
                                opacity="0.7"
                                borderRadius="10px"
                              >
                                <Text
                                  color="#fff"
                                  fontSize="24px"
                                  fontFamily="FedraSansStd-medium"
                                >
                                  FULL
                                </Text>
                              </Box>
                            )}
                            {/* <style>
                              {`
                                .radio{
                                  border:0 !important;
                                  background:transparent !important;
                                }
                                .radio:before{
                                  background:#CF4520 !important;
                                }
                                `}
                            </style> */}
                            <Flex justifyContent="space-between">
                              <Box
                                fontFamily={
                                  lang === 'ta'
                                    ? 'MuktaMalar'
                                    : 'FedraSansStd-medium'
                                }
                                fontSize={{ base: '14px', md: '20px' }}
                                fontWeight={lang === 'ta' ? 'normal' : '500'}
                                fontStyle="normal"
                                lineHeight="22px"
                                display="flex"
                                alignItems="center"
                                fontFeatureSettings="case"
                                color="#000000"
                                width={{base:'100%', md: lang === 'ta' ? '100%' : '75%'}}
                              >
                                {data.Category}
                              </Box>
                              <Radio
                                name="inComp"
                                id="radio1"
                                className="radio"
                                colorScheme="orange"
                                value={data.Category}
                                boxShadow="0 0 0 1px #CF4520"
                                width="20px"
                                height="20px"
                                key={data.RefName}
                                cursor="pointer"
                                isDisabled={
                                  data?.Status?.toLowerCase() === 'full'
                                    ? true
                                    : false
                                }
                              />
                            </Flex>
                          </Box>
                        );
                      })}
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </Grid>
              <MidContainer w={{ base: '100%', md: '660px' }}>
                <Box m="30px auto 0 auto" ml={{ base: '0', md: '30px' }}>
                  {progLocation === 'regional-center' && yesUrl != '' && (
                    <StructuredTextParser
                      str={lang === 'ta' ? render(sectionContent[20]?.body) : render(sectionContent[16]?.body)}
                      region={region}
                      lang={lang}
                      fontSize="24px"
                      color="#000000"
                      fontFamily={
                        lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'
                      }
                      mt="0px"
                      textAlign="center"
                      className="notetext"
                      mb={{base: '10px', md: "30px"}}
                    />
                  )}
                  {progLocation === 'in-person' ? (
                    <>
                      <StructuredTextParser
                        str={lang === 'ta' ? render(sectionContent[20]?.body) : render(sectionContent[16]?.body)}
                        region={region}
                        lang={lang}
                        fontSize="24px"
                        color="#000000"
                        fontFamily={
                          lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'
                        }
                        mt="0px"
                        textAlign="center"
                        className="notetext"
                        mb={{base: '10px', md: "30px"}}
                      />
                      <ProgramDetailsLayout jsonData={sectionContent[17]?.data} progLocation={progLocation} lang={lang}/>
                      {searchByFieldId(sectionContent, 'iyc_dais') && (
                        <>
                          <Box textAlign={{base:'center', md:'left'}}>
                            <LazyLoadImageComponent
                              src={
                                searchByFieldId(sectionContent, 'iyc_dais').image
                                  ?.url
                              }
                              alt={
                                searchByFieldId(sectionContent, 'iyc_dais').image
                                  ?.alt
                              }
                              maxW={{ base: '320px', md: '620px' }}
                              maxH={{ base: '240px', md: '465px' }}
                            />
                          </Box>
                        </>
                      )}
                      <Grid py="40px">
                        <FormControl>
                          <RadioGroup
                            value={yesUrl2}
                            onChange={event => setYesUrl2(event.target.value)}
                          >
                            <Flex
                              placeContent="center"
                              gridGap={{ base: '7px', md: '30px' }}
                              gridRowGap={{ base: '15px', md: '30px' }}
                              mt="10px"
                              flexWrap='wrap'
                              flexDirection="row"
                              width={{ lg: '940px' }}
                              ml={{ lg: '-170px' }}

                            >
                              {sectionContent[23]?.data?.map((data, i) => {
                                return (
                                  <Box
                                    visibility={!data?.Category ? 'hidden' : ''}
                                    as="label"
                                    key={nanoid(4)}
                                    w={{ base: '40%', md: '279px' }}
                                    maxW="279px"
                                    minW={{ base: '47%', md: '279px' }}
                                    paddingLeft={{
                                      base: '12.14px',
                                      md: '21.18px',
                                    }}
                                    minH={{ base: '160px', md: '250px' }}
                                    pr={{ base: '11.47px', md: '20px' }}
                                    pt={{ base: '16.5px', md: '29px' }}
                                    pb={{ base: '19px', md: '27px' }}
                                    style={
                                      yesUrl2 === data.LinkUrl
                                        ? cardStyle2.Selected
                                        : data?.Status?.toLowerCase() === 'full'
                                          ? cardStyle2.Full
                                          : cardStyle2.Open
                                    }
                                    position="relative"
                                    borderRadius="10px"
                                    onClick={event =>
                                      handleRadioClick2(event, data)
                                    }
                                    cursor={
                                      data?.Status?.toLowerCase() === 'full'
                                        ? 'cursor'
                                        : 'pointer'
                                    }
                                    _hover={
                                      {}
                                    }
                                  >
                                    {data?.Category ? <>

                                      {data?.Status?.toLowerCase() === 'full' && (
                                        <Box
                                          pos="absolute"
                                          top={0}
                                          right={0}
                                          left={0}
                                          bottom={0}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          textAlign="center"
                                          background="#494949"
                                          opacity="0.7"
                                          borderRadius="10px"
                                        >
                                          <Text
                                            color="#fff"
                                            fontSize="24px"
                                            fontFamily="FedraSansStd-medium"
                                          >
                                            FULL
                                          </Text>
                                        </Box>
                                      )}
                                      <style>
                                        {`
                                  .radio{
                                    border:0 !important;
                                    background:transparent !important;
                                  }
                                  .radio:before{
                                    background:#CF4520 !important;
                                  }
                                  `}
                                      </style>
                                      <Flex justifyContent="space-between">
                                        <Box
                                          fontFamily="FedraSansStd-medium"
                                          fontStyle="normal"
                                          fontWeight="500"
                                          fontSize={{ md: '20px', base: '14px' }}
                                          lineHeight="16px"
                                          display="flex"
                                          alignItems="center"
                                          fontFeatureSettings="case"
                                          color="#000000"
                                        >
                                          {data.Category}
                                        </Box>
                                        <Radio
                                          name="inComp"
                                          id="radio1"
                                          className="radio"
                                          colorScheme="orange"
                                          value={data.LinkUrl}
                                          boxShadow="0 0 0 1px #CF4520"
                                          width="26px"
                                          height="26px"
                                          key={data.Category}
                                          cursor="pointer"
                                          isDisabled={
                                            data?.Status?.toLowerCase() === 'full'
                                              ? true
                                              : false
                                          }
                                        />
                                      </Flex>
                                      {data?.EBText && (
                                        <Box
                                          color={'#B40000'}
                                          fontFamily="FedraSansStd-book"
                                          fontSize={{ base: '10.46px', md: '18px' }}
                                        >
                                          {data.EBText}
                                        </Box>
                                      )}
                                      <Box
                                        mt={{base:'0px', md:'20px'}}
                                        mb={{base:'15px', md:'20px'}}
                                        fontFamily="FedraSansStd-book"
                                        background="#E4DED4"
                                        borderRadius="4px"
                                        p={{ sm: '4px 12px', base: '5px' }}
                                        fontSize={{ md: '18px', base: '10.5px' }}
                                        color="#000"
                                        textAlign="left"
                                        w="auto"
                                        display="inline-flex"
                                      >
                                        {data.NormalPrice !== data.EBPrice && (
                                          <Box
                                            as="span"
                                            textDecoration="line-through"
                                            mr="10px"
                                          >
                                            {data.NormalPrice}
                                          </Box>
                                        )}

                                        {data.EBPrice}
                                      </Box>
                                      <Box>
                                        <UnorderedList
                                          fontStyle="normal"
                                          fontWeight="400"
                                          fontSize={{ md: '16px', base: '12px' }}
                                          lineHeight={{ md: '20px', base: '16px' }}
                                          fontFeatureSettings="case"
                                          color="#28231E"
                                          fontFamily="FedraSansStd-book"
                                        >
                                          {data.Descripton.value_1 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_1 || ''
                                              )}
                                            </ListItem>
                                          )}
                                          {data.Descripton.value_2 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_2 || ''
                                              )}
                                            </ListItem>
                                          )}
                                          {data.Descripton.value_3 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_3 || ''
                                              )}
                                            </ListItem>
                                          )}
                                          {data.Descripton.value_4 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_4 || ''
                                              )}
                                            </ListItem>
                                          )}
                                        </UnorderedList>
                                      </Box>
                                    </> : null}
                                  </Box>
                                );
                              })}
                            </Flex>
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </>
                  ) : 
                  <ProgramDetailsLayout jsonData={sectionContent[17]?.data} progLocation={progLocation} lang={lang}/>}
                </Box>
                {progLocation === 'regional-center' ? 
                <Box mt={{md: '2rem'}}>
                  <SingleLineText
                    textAlign={'center'}
                    sectionContent={sectionContent[21]}
                    fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                    fontWeight={600}
                    fontSize="20px"
                    my={{ base: '0px', md: '0px' }}
                    color="#02026C"
                    />
                    <Flex mt={{base:'1.6rem', md: '2rem'}} width={'100%'} justifyContent={'center'} >
                      <Flex width={{base: '190px', md: '406px'}} flexDir={{base:'column', md: 'row'}} gap={{base:'1rem', md: '0'}} justifyContent={'space-between'}>
                      <ResponsiveSelect
                        options={districts}
                        onChange={e => {                          
                          let districtWithSortedCities = e
                          setSelectedDistrict({ ...districtWithSortedCities })
                          setSelectedCity({})
                        }}
                        selectedOption={selectedDistrict}
                        placeholder={lang === 'en' ? 'District' : 'மாவட்டம்'}
                        popupPlaceHolderTitle={lang === 'en' ? 'Choose your district' : 'உங்கள் மாவட்டத்தைத் தேர்வுசெய்யவும்'}
                        lang={lang}
                        label={lang === 'ta' ? 'district_ta' : 'district_en'}
                        value={'district_en'}
                      />
                      <ResponsiveSelect
                        options={selectedDistrict?.cities}
                        onChange={e => {
                          setSelectedCity({ ...e })
                          if (e?.city_en) {
                            let updateParam = (url, param, value) => {
                              const u = new URL(url);
                              u.searchParams.set(param, value);
                              return u.toString();
                            };
                            // let updated = selectedProg?.LinkUrl.replace(/(uat|prod)='(.*?)'/g, (match, env, url) => {
                            //   const updatedUrl = updateParam(url, 'freeTextSearch', e?.city_en); // ← your dynamic value here
                            //   return `${env}='${updatedUrl}'`;
                            // });
                            let updated = e.mediator_link
                            setSelectedProg({...selectedProg, LinkUrl: updated})
                          }
                        }}
                        selectedOption={selectedCity}
                        placeholder={lang === 'en' ? 'City' : 'நகரம்'}
                        popupPlaceHolderTitle={lang === 'en' ? 'Choose your city' : 'உங்கள் நகரத்தைத் தேர்வுசெய்யவும்'}
                        lang={lang}
                        label={lang==='ta' ? 'city_ta' : 'city_en'}
                        value={'city_en'}
                      />
                      </Flex>
                    </Flex>
                  </Box> : null}
              </MidContainer>
              <Box display="flex" placeContent="center" mt="40px">
                <Link
                  href={ yesUrl2 ? updateUrlForInPerson(yesUrl2, true) :
                    ((selectedProg?.LinkUrl && selectedCity?.city_en)
                      ? refineUrlDomain(
                          selectedProg?.LinkUrl,
                          region,
                          lang
                        )
                      : 'javascript:void()')
                  }
                  mb="20px"
                  display="block"
                  fontWeight={'500'}
                  fontFamily={'FedraSansStd-medium'}
                  fontSize={'18px'}
                  bgColor={(yesUrl && selectedCity?.city_en && progLocation === 'regional-center') || (yesUrl2 && progLocation === 'in-person') ? '#CF4520' : '#333333'}
                  opacity={(yesUrl && selectedCity?.city_en && progLocation === 'regional-center') || (yesUrl2 && progLocation === 'in-person') ? '1' : '0.4'}
                  h={'auto'}
                  minH="50px"
                  textAlign="left"
                  padding={'14px 29px'}
                  borderRadius="5px"
                  color="#faf7f0"
                  textDecoration="none"
                  width={'auto'}
                  cursor={(yesUrl && selectedCity?.city_en && progLocation === 'regional-center') || (yesUrl2 && progLocation === 'in-person') ? 'pointer' : 'not-allowed'}
                  _hover={{
                    background: (yesUrl && selectedCity?.city_en && progLocation === 'regional-center') || (yesUrl2 && progLocation === 'in-person') ? '#000054' : '#333333',
                  }}
                  target={(yesUrl && selectedCity?.city_en && progLocation === 'regional-center') || (yesUrl2 && progLocation === 'in-person')  ? '_blank' : ''}
                  // onClick={() => {
                  //   gtmClick();
                  // }}
                >
                  {selectedProg?.CTAButton || sectionContent[18]?.buttonText}
                </Link>
              </Box>
            </TabPanel>

            {/* Tab 2 starts here NO */}
            <TabPanel px={{ base: '15px', md: '0' }} pt="20px" pb="60px">
              <Box
                color="#02026C"
                fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                fontSize={{base: '18px', md:"22px"}}
                lineHeight={'30px'}
                py={{ base: '20px', md: '40px' }}
                px={{ base: '15px', md: '30px' }}
                fontWeight={lang === 'ta' ? 'normal' : 'bold'}
                textAlign="center"
                maxW="630px"
                mx="auto"
              >
                {sectionContent[14]?.text}
              </Box>
              <Grid pt="10px" justifyContent={'center'}>
                <FormControl>
                  <RadioGroup
                    onChange={event => setNoUrl(event.target.value)}
                    value={noUrl}
                  >
                    <Flex
                      placeContent="center"
                      gridGap={{ base: '7px', md: '30px' }}
                      gridRowGap={{ base: '15px', md: '30px' }}
                      mt="10px"
                      maxW="700px"
                      flexWrap="wrap"
                      flexDirection="row"
                    >
                      {sectionContent[15]?.data?.map((data, i) => {
                        return (
                          <Box
                            as="label"
                            key={nanoid(4)}
                            w={{ base: '40%', md: '279px' }}
                            maxW="279px"
                            minW={{ base: '47%', md: '160px' }}
                            // minH={{ base: '160px', md: '250px' }}
                            paddingLeft={{
                              base: '12.14px',
                              md: '21.18px',
                            }}
                            border="1px solid #979797"
                            borderRadius="10px"
                            pr={{ base: '11.47px', md: '20px' }}
                            pt={{ base: '16.5px', md: '29px' }}
                            pb={{ base: '19px', md: '27px' }}
                            height="auto"
                            style={
                              yesUrl === data.Category
                                ? cardStyle.Selected
                                : data?.Status?.toLowerCase() === 'full'
                                ? cardStyle.Full
                                : cardStyle.Open
                            }
                            _hover={{
                              boxShadow:
                                '3.06087px 18.7518px 41px rgba(34, 30, 2, 0.08), 1.98389px 12.154px 24.0116px rgba(34, 30, 2, 0.0607407), 1.179px 7.22293px 13.0593px rgba(34, 30, 2, 0.0485926), 0.612173px 3.75037px 6.6625px rgba(34, 30, 2, 0.04), 0.249404px 1.52793px 3.34074px rgba(34, 30, 2, 0.0314074), 0.0566827px 0.347256px 1.61343px rgba(34, 30, 2, 0.0192593)',
                            }}
                            position="relative"
                            onClick={event => handleRadioClick(event, data)}
                            cursor={
                              data?.Status?.toLowerCase() === 'full'
                                ? 'cursor'
                                : 'pointer'
                            }
                          >
                            {data?.Status?.toLowerCase() === 'full' && (
                              <Box
                                pos="absolute"
                                top={0}
                                right={0}
                                left={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                background="#494949"
                                opacity="0.7"
                                borderRadius="10px"
                              >
                                <Text
                                  color="#fff"
                                  fontSize="24px"
                                  fontFamily="FedraSansStd-medium"
                                >
                                  FULL
                                </Text>
                              </Box>
                            )}
                            <style>
                              {`
                                .radio{
                                  border:0 !important;
                                  background:transparent !important;
                                }
                                .radio:before{
                                  background:#CF4520 !important;
                                }
                                `}
                            </style>
                            <Flex justifyContent="space-between">
                              <Box
                                fontFamily={
                                  lang === 'ta'
                                    ? 'MuktaMalar'
                                    : "'FedraSansStd-medium'"
                                }
                                fontSize={{ base: '14px', md: '20px' }}
                                fontWeight={lang === 'ta' ? 'normal' : '500'}
                                fontStyle="normal"
                                lineHeight="22px"
                                display="flex"
                                alignItems="center"
                                fontFeatureSettings="case"
                                color="#000000"
                                width={{base:'100%', md: lang === 'ta' ? '100%' : '75%'}}
                              >
                                {data.Category}
                              </Box>
                              <Radio
                                name="inComp"
                                id="radio1"
                                className="radio"
                                colorScheme="orange"
                                value={data.Category}
                                boxShadow="0 0 0 1px #CF4520"
                                width="20px"
                                height="20px"
                                key={data.RefName}
                                cursor="pointer"
                                isDisabled={
                                  data?.Status?.toLowerCase() === 'full'
                                    ? true
                                    : false
                                }
                              />
                            </Flex>
                          </Box>
                        );
                      })}
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </Grid>
              <MidContainer w={{ base: '100%', md: '660px' }}>
                <Box m="30px auto 0 auto" ml={{ base: '0', md: '30px' }}>
                  {progLocation === 'regional-center' && noUrl != '' && (
                    <StructuredTextParser
                      str={lang === 'en' ? render(sectionContent[20]?.body) : render(sectionContent[16]?.body)}
                      region={region}
                      lang={lang}
                      fontSize="24px"
                      color="#000000"
                      fontFamily={
                        lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'
                      }
                      mt="0px"
                      textAlign="center"
                      className="notetext"
                      mb={{base: '10px', md: "30px"}}
                    />
                  )}
                  {progLocation === 'in-person' ? (
                    <>
                      <StructuredTextParser
                        str={lang === 'en' ? render(sectionContent[20]?.body) : render(sectionContent[16]?.body)}
                        region={region}
                        lang={lang}
                        fontSize="24px"
                        color="#000000"
                        fontFamily={
                          lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'
                        }
                        mt="0px"
                        textAlign="center"
                        className="notetext"
                        mb={{base: '10px', md: "30px"}}
                      />
                      <ProgramDetailsLayout jsonData={sectionContent[17]?.data} progLocation={progLocation} lang={lang}/>
                      {searchByFieldId(sectionContent, 'iyc_dais') && (
                        <>
                          <Box textAlign={{base:'center', md:'left'}}>
                            <LazyLoadImageComponent
                              src={
                                searchByFieldId(sectionContent, 'iyc_dais').image
                                  ?.url
                              }
                              alt={
                                searchByFieldId(sectionContent, 'iyc_dais').image
                                  ?.alt
                              }
                              maxW={{ base: '320px', md: '620px' }}
                              maxH={{ base: '240px', md: '465px' }}
                            />
                          </Box>
                        </>
                      )}
                      <Grid py="40px">
                        <FormControl>
                          <RadioGroup
                            value={noUrl2}
                            onChange={event => setNoUrl2(event.target.value)}
                          >
                            <Flex
                              placeContent="center"
                              gridGap={{ base: '7px', md: '30px' }}
                              gridRowGap={{ base: '15px', md: '30px' }}
                              mt="10px"
                              flexWrap='wrap'
                              flexDirection="row"
                              width={{ lg: '940px' }}
                              ml={{ lg: '-170px' }}

                            >
                              {sectionContent[23]?.data?.map((data, i) => {
                                return (
                                  <Box
                                    visibility={!data?.Category ? 'hidden' : ''}
                                    as="label"
                                    key={nanoid(4)}
                                    w={{ base: '40%', md: '279px' }}
                                    maxW="279px"
                                    minW={{ base: '47%', md: '279px' }}
                                    paddingLeft={{
                                      base: '12.14px',
                                      md: '21.18px',
                                    }}
                                    minH={{ base: '160px', md: '250px' }}
                                    pr={{ base: '11.47px', md: '20px' }}
                                    pt={{ base: '16.5px', md: '29px' }}
                                    pb={{ base: '19px', md: '27px' }}
                                    style={
                                      noUrl2 === data.LinkUrl
                                        ? cardStyle2.Selected
                                        : data?.Status?.toLowerCase() === 'full'
                                          ? cardStyle2.Full
                                          : cardStyle2.Open
                                    }
                                    position="relative"
                                    borderRadius="10px"
                                    onClick={event =>
                                      handleRadioClick2(event, data)
                                    }
                                    cursor={
                                      data?.Status?.toLowerCase() === 'full'
                                        ? 'cursor'
                                        : 'pointer'
                                    }
                                    _hover={
                                      {}
                                    }
                                  >
                                    {data?.Category ? <>

                                      {data?.Status?.toLowerCase() === 'full' && (
                                        <Box
                                          pos="absolute"
                                          top={0}
                                          right={0}
                                          left={0}
                                          bottom={0}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          textAlign="center"
                                          background="#494949"
                                          opacity="0.7"
                                          borderRadius="10px"
                                        >
                                          <Text
                                            color="#fff"
                                            fontSize="24px"
                                            fontFamily="FedraSansStd-medium"
                                          >
                                            FULL
                                          </Text>
                                        </Box>
                                      )}
                                      <style>
                                        {`
                                  .radio{
                                    border:0 !important;
                                    background:transparent !important;
                                  }
                                  .radio:before{
                                    background:#CF4520 !important;
                                  }
                                  `}
                                      </style>
                                      <Flex justifyContent="space-between">
                                        <Box
                                          fontFamily="FedraSansStd-medium"
                                          fontStyle="normal"
                                          fontWeight="500"
                                          fontSize={{ md: '20px', base: '14px' }}
                                          lineHeight="16px"
                                          display="flex"
                                          alignItems="center"
                                          fontFeatureSettings="case"
                                          color="#000000"
                                        >
                                          {data.Category}
                                        </Box>
                                        <Radio
                                          name="inComp"
                                          id="radio1"
                                          className="radio"
                                          colorScheme="orange"
                                          value={data.LinkUrl}
                                          boxShadow="0 0 0 1px #CF4520"
                                          width="26px"
                                          height="26px"
                                          key={data.Category}
                                          cursor="pointer"
                                          isDisabled={
                                            data?.Status?.toLowerCase() === 'full'
                                              ? true
                                              : false
                                          }
                                        />
                                      </Flex>
                                      {data?.EBText && (
                                        <Box
                                          color={'#B40000'}
                                          fontFamily="FedraSansStd-book"
                                          fontSize={{ base: '10.46px', md: '18px' }}
                                        >
                                          {data.EBText}
                                        </Box>
                                      )}
                                      <Box
                                        mt={{base:'0px', md:'20px'}}
                                        mb={{base:'15px', md:'20px'}}
                                        fontFamily="FedraSansStd-book"
                                        background="#E4DED4"
                                        borderRadius="4px"
                                        p={{ sm: '4px 12px', base: '5px' }}
                                        fontSize={{ md: '18px', base: '10.5px' }}
                                        color="#000"
                                        textAlign="left"
                                        w="auto"
                                        display="inline-flex"
                                      >
                                        {data.NormalPrice !== data.EBPrice && (
                                          <Box
                                            as="span"
                                            textDecoration="line-through"
                                            mr="10px"
                                          >
                                            {data.NormalPrice}
                                          </Box>
                                        )}

                                        {data.EBPrice}
                                      </Box>
                                      <Box>
                                        <UnorderedList
                                          fontStyle="normal"
                                          fontWeight="400"
                                          fontSize={{ md: '16px', base: '12px' }}
                                          lineHeight={{ md: '20px', base: '16px' }}
                                          fontFeatureSettings="case"
                                          color="#28231E"
                                          fontFamily="FedraSansStd-book"
                                        >
                                          {data.Descripton.value_1 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_1 || ''
                                              )}
                                            </ListItem>
                                          )}
                                          {data.Descripton.value_2 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_2 || ''
                                              )}
                                            </ListItem>
                                          )}
                                          {data.Descripton.value_3 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_3 || ''
                                              )}
                                            </ListItem>
                                          )}
                                          {data.Descripton.value_4 && (
                                            <ListItem mb="10px">
                                              {parser(
                                                data.Descripton.value_4 || ''
                                              )}
                                            </ListItem>
                                          )}
                                        </UnorderedList>
                                      </Box>
                                    </> : null}
                                  </Box>
                                );
                              })}
                            </Flex>
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </>
                  ) : 
                  <ProgramDetailsLayout jsonData={sectionContent[17]?.data} progLocation={progLocation} lang={lang}/>}
                </Box>
                {progLocation === 'regional-center' ? 
                <Box my={{md: '2rem'}}>
                  <SingleLineText
                    textAlign={'center'}
                    sectionContent={sectionContent[21]}
                    fontFamily={lang === 'ta' ? 'MuktaMalar' : 'FedraSansStd-book'}
                    fontWeight={600}
                    fontSize="20px"
                    my={{ base: '0px', md: '0px' }}
                    color="#02026C"
                    />
                    <Flex mt={{base:'1.6rem', md: '2rem'}} width={'100%'} justifyContent={'center'} >
                      <Flex width={{base: '190px', md: '406px'}} flexDir={{base:'column', md: 'row'}} gap={{base:'1rem', md: '0'}} justifyContent={'space-between'}>
                      <ResponsiveSelect
                        options={districts}
                        onChange={e => {                         
                          let districtWithSortedCities = e
                          setSelectedDistrict({ ...districtWithSortedCities })
                          setSelectedCity({})
                        }}
                        selectedOption={selectedDistrict}
                        placeholder={lang === 'en' ? 'District' : 'மாவட்டம்'}
                        popupPlaceHolderTitle={lang === 'en' ? 'Choose your district' : 'உங்கள் மாவட்டத்தைத் தேர்வுசெய்யவும்'}
                        lang={lang}
                        label={lang === 'ta' ? 'district_ta' : 'district_en'}
                        value={'district_en'}
                      />
                      <ResponsiveSelect
                        options={selectedDistrict?.cities}
                        onChange={e => {
                          setSelectedCity({ ...e })
                          if (e?.city_en) {
                            let updateParam = (url, param, value) => {
                              const u = new URL(url);
                              u.searchParams.set(param, value);
                              return u.toString();
                            };
                            // let updated = selectedProg?.LinkUrl.replace(/(uat|prod)='(.*?)'/g, (match, env, url) => {
                            //   const updatedUrl = updateParam(url, 'freeTextSearch', e?.city_en); // ← your dynamic value here
                            //   return `${env}='${updatedUrl}'`;
                            // });
                            let updated = e.non_mediator_link
                            setSelectedProg({...selectedProg, LinkUrl: updated})
                          }
                        }}
                        selectedOption={selectedCity}
                        placeholder={lang === 'en' ? 'City' : 'நகரம்'}
                        popupPlaceHolderTitle={lang === 'en' ? 'Choose your city' : 'உங்கள் நகரத்தைத் தேர்வுசெய்யவும்'}
                        lang={lang}
                        label={lang==='ta' ? 'city_ta' : 'city_en'}
                        value={'city_en'}
                      />
                      </Flex>
                    </Flex>
                  </Box> : null}
              </MidContainer>
              <Box
                display="flex"
                placeContent="center"
                mt={{ base: '-20px', md: '10px', lg: '-20px' }}
              >
                <Link
                  href={noUrl2 ? updateUrlForInPerson(noUrl2, false) :
                    ((selectedProg?.LinkUrl && selectedCity?.city_en)
                      ? refineUrlDomain(
                          selectedProg?.LinkUrl,
                          region,
                          lang
                        )
                      : 'javascript:void()')
                  }
                  my="30px"
                  display="block"
                  fontWeight={'500'}
                  fontFamily={'FedraSansStd-medium'}
                  fontSize={'18px'}
                  bgColor={(noUrl && selectedCity?.city_en && progLocation === 'regional-center') || (noUrl2 && progLocation === 'in-person') ? '#cf4520' : '#cf452069'}
                  h={'auto'}
                  minH="50px"
                  textAlign="left"
                  padding={'14px 29px'}
                  borderRadius="5px"
                  color="#faf7f0"
                  textDecoration="none"
                  width={'auto'}
                  cursor={(noUrl && selectedCity?.city_en && progLocation === 'regional-center') || (noUrl2 && progLocation === 'in-person') ? 'pointer' : 'not-allowed'}
                  _hover={{
                    bg: (noUrl && selectedCity?.city_en && progLocation === 'regional-center') || (noUrl2 && progLocation === 'in-person') ? '#000054' : '',
                  }}
                  target={(noUrl && selectedCity?.city_en && progLocation === 'regional-center') || (noUrl2 && progLocation === 'in-person') ? '_blank' : ''}
                  // onClick={() => {
                  //   gtmClick();
                  //   ``;
                  // }}
                >
                  {selectedProg?.CTAButton || sectionContent[18]?.buttonText}
                </Link>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      <br />
      <style jsx global>
        {`
          .alink a mark,
          .alink p mark {
            background: none;
          }
          .alink a mark {
            color: #cf4520;
          }
          .notetext p {
            font-size: 18px;
            color: #28231e;
          }
          .notetext mark {
            color: #02026c;
            background: none;
            font-weight: 500;
            font-family: FedraSansStd-medium;
          }
          .instructtext {
            margin-bottom: 30px;
          }
          .instructtext p {
            color: #cf4520;
            font-size: 18px;
            padding: 5px 0;
          }
          .instructtext p {
            background: url('https://static.sadhguru.org/d/46272/1744372986-clock_icon.png')
              no-repeat left center;
            padding-left: 24px;
            background-size: 16px;
          }
          .instructtext mark {
            background: none;
            color: #000000;
          }
          @media only screen and (max-width: 360px) {
            .gridComponent {
              grid-template-columns: repeat(1, minmax(0, 1fr));
              margin-top: 10px;
            }
            .linkText p {
              text-align: center !important;
            }
          }
          @media only screen and (max-width: 736px) {
            .linkText {
              width: 300px;
              margin: 0 auto;
            }
            .linkText p {
              text-align: center !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default ProgramDetailsLOM;
